import React, { useState, useRef, useEffect } from 'react';
import './App.css';

// 🔥 Central API URL — best practice
const API_URL = process.env.REACT_APP_API_URL || "https://travel-rag-worker-full.vinod-jrsci-5d6.workers.dev";

function App() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI travel assistant with access to comprehensive travel knowledge. I can help you discover destinations, plan trips, and book flights, hotels, and cabs. Where would you like to go?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // 🔥 Updated Chat API call using API_URL
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: input,
          conversation_history: messages
        }),
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        intent: data.intent,
        requiresBooking: data.requires_booking
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    return content
      .split('\n').map((line, i) => {
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
          return `<li>${line.substring(1)}</li>`;
        }
        return line ? `<p>${line}</p>` : '<br/>';
      }).join('');
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <span className="logo-icon">✈️</span>
            <h1>AI Travel Assistant</h1>
            <span className="badge">Powered by RAG + 70B LLM</span>
          </div>
          <nav className="nav-tabs">
            <button 
              className={activeTab === 'chat' ? 'active' : ''}
              onClick={() => setActiveTab('chat')}
            >
              💬 Chat
            </button>
            <button 
              className={activeTab === 'flights' ? 'active' : ''}
              onClick={() => setActiveTab('flights')}
            >
              ✈️ Flights
            </button>
            <button 
              className={activeTab === 'hotels' ? 'active' : ''}
              onClick={() => setActiveTab('hotels')}
            >
              🏨 Hotels
            </button>
            <button 
              className={activeTab === 'cabs' ? 'active' : ''}
              onClick={() => setActiveTab('cabs')}
            >
              🚗 Cabs
            </button>
          </nav>
        </div>
      </header>

      <div className="main-container">
        {activeTab === 'chat' && (
          <div className="chat-container">
            <div className="messages-container">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="avatar">🤖</div>
                  )}
                  <div className="message-content">
                    <div 
                      dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                    />
                    {message.intent && message.intent.query_type === 'booking' && (
                      <div className="intent-badge">
                        🎫 Booking Query Detected
                      </div>
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div className="avatar user-avatar">👤</div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="message assistant-message">
                  <div className="avatar">🤖</div>
                  <div className="message-content loading">
                    <div className="loading-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span>Thinking with AI...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="input-container">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask me anything about travel... (e.g., 'Best beaches in India?')"
                disabled={isLoading}
              />
              <button 
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                className="send-button"
              >
                {isLoading ? '...' : '➤'}
              </button>
            </div>

            <div className="features-info">
              <span>💡 Powered by RAG: I have knowledge of 12+ destinations</span>
              <span>🎯 Smart Intent: I know when you want info vs bookings</span>
              <span>🚀 70B LLM: Advanced AI for natural conversations</span>
            </div>
          </div>
        )}

        {activeTab !== 'chat' && (
          <div className="coming-soon">
            <h2>🚧 {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Search</h2>
            <p>This feature is coming soon!</p>
            <p>For now, ask me in the chat and I'll help you find the best options.</p>
            <button onClick={() => setActiveTab('chat')}>
              Go to Chat →
            </button>
          </div>
        )}
      </div>

      <footer className="footer">
        <p>© 2025 AI Travel Assistant | Powered by RAG + 70B LLM</p>
        <div className="status-indicator">
          <span className="status-dot"></span>
          System Online
        </div>
      </footer>
    </div>
  );
}

export default App;

